import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Truck, Search, Plus, User, Phone, MapPin, Clock, 
  CheckCircle, XCircle, RefreshCw, MoreHorizontal, Car, Bike,
  Navigation, Package, Calendar, UserPlus, BarChart3, Activity, Info, Route
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import LanguageSwitcher from '../components/LanguageSwitcher';
import RouteEditorPanel from '../components/RouteEditorPanel';
import { hasPermission } from '../utils/permissions';

function AdminDelivery({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();
  
  const canEditRoutes = hasPermission(user?.permissions, 'delivery.routes');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [ops, setOps] = useState(null);
  const [opsDays, setOpsDays] = useState('30');
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Couriers data
  const [couriers, setCouriers] = useState([]);
  const [showCourierDialog, setShowCourierDialog] = useState(false);
  const [editingCourier, setEditingCourier] = useState(null);
  const [courierForm, setCourierForm] = useState({
    name: '',
    phone: '',
    email: '',
    vehicle_type: 'car',
    is_active: true
  });
  
  // Deliveries data
  const [deliveries, setDeliveries] = useState([]);
  const [deliveryFilters, setDeliveryFilters] = useState({
    status: 'all',
    courier_id: 'all',
    date: new Date().toISOString().split('T')[0]
  });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);

  const VEHICLE_TYPES = [
    { value: 'car', label: 'Автомобиль', icon: Car },
    { value: 'bike', label: 'Мотоцикл', icon: Bike },
    { value: 'walk', label: 'Пешком', icon: User },
  ];

  const DELIVERY_STATUSES = [
    { value: 'pending', label: 'Ожидает', color: 'bg-gray-100 text-gray-800' },
    { value: 'assigned', label: 'Назначен', color: 'bg-blue-100 text-blue-800' },
    { value: 'in_transit', label: 'В пути', color: 'bg-purple-100 text-purple-800' },
    { value: 'delivered', label: 'Доставлен', color: 'bg-green-100 text-green-800' },
    { value: 'failed', label: 'Не доставлен', color: 'bg-red-100 text-red-800' },
    { value: 'cancelled', label: 'Отменён', color: 'bg-gray-100 text-gray-800' },
  ];

  useEffect(() => {
    fetchDashboard();
    fetchCouriers();
  }, []);

  useEffect(() => {
    if (activeTab === 'deliveries') {
      fetchDeliveries();
    }
  }, [activeTab, page, deliveryFilters]);

  useEffect(() => {
    if (activeTab === 'ops') fetchOps();
    // eslint-disable-next-line
  }, [activeTab, opsDays]);

  const fetchOps = async () => {
    try {
      const res = await axios.get(`${API}/analytics/delivery-ops?days=${opsDays}`, { headers: getAuthHeaders() });
      setOps(res.data);
    } catch (e) {
      console.error('Failed to fetch delivery ops');
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/deliveries/dashboard`, {
        headers: getAuthHeaders()
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchCouriers = async () => {
    try {
      const response = await axios.get(`${API}/admin/couriers`, {
        headers: getAuthHeaders()
      });
      setCouriers(response.data);
    } catch (error) {
      toast.error('Не удалось загрузить курьеров');
    }
  };

  const fetchDeliveries = async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(deliveryFilters.status !== 'all' && { status: deliveryFilters.status }),
        ...(deliveryFilters.courier_id !== 'all' && { courier_id: deliveryFilters.courier_id }),
        ...(deliveryFilters.date && { date: deliveryFilters.date }),
      });
      
      const response = await axios.get(`${API}/admin/deliveries?${params}`, {
        headers: getAuthHeaders()
      });
      
      setDeliveries(response.data.deliveries);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error('Не удалось загрузить доставки');
    }
  };

  const handleSaveCourier = async (e) => {
    e.preventDefault();
    if (!courierForm.name || !courierForm.phone) {
      toast.error('Заполните обязательные поля');
      return;
    }
    
    try {
      if (editingCourier) {
        await axios.put(`${API}/admin/couriers/${editingCourier.id}`, courierForm, {
          headers: getAuthHeaders()
        });
        toast.success('Курьер обновлён');
      } else {
        await axios.post(`${API}/admin/couriers`, courierForm, {
          headers: getAuthHeaders()
        });
        toast.success('Курьер добавлен');
      }
      setShowCourierDialog(false);
      resetCourierForm();
      fetchCouriers();
      fetchDashboard();
    } catch (error) {
      toast.error('Не удалось сохранить курьера');
    }
  };

  const handleDeleteCourier = async (courierId) => {
    if (!confirm('Удалить курьера?')) return;
    
    try {
      await axios.delete(`${API}/admin/couriers/${courierId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Курьер удалён');
      fetchCouriers();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось удалить курьера');
    }
  };

  const handleAssignDelivery = async (courierId) => {
    try {
      await axios.put(`${API}/admin/deliveries/${selectedDelivery.id}/assign?courier_id=${courierId}`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Курьер назначен');
      setShowAssignDialog(false);
      setSelectedDelivery(null);
      fetchDeliveries();
      fetchDashboard();
    } catch (error) {
      toast.error('Не удалось назначить курьера');
    }
  };

  const handleDeliveryStatus = async (deliveryId, status) => {
    try {
      await axios.put(`${API}/admin/deliveries/${deliveryId}/status?status=${status}`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Статус обновлён');
      fetchDeliveries();
      fetchDashboard();
    } catch (error) {
      toast.error('Не удалось обновить статус');
    }
  };

  const openEditCourier = (courier) => {
    setEditingCourier(courier);
    setCourierForm({
      name: courier.name,
      phone: courier.phone,
      email: courier.email || '',
      vehicle_type: courier.vehicle_type,
      is_active: courier.is_active
    });
    setShowCourierDialog(true);
  };

  const resetCourierForm = () => {
    setEditingCourier(null);
    setCourierForm({
      name: '',
      phone: '',
      email: '',
      vehicle_type: 'car',
      is_active: true
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusConfig = (status) => {
    return DELIVERY_STATUSES.find(s => s.value === status) || DELIVERY_STATUSES[0];
  };

  const getVehicleConfig = (type) => {
    return VEHICLE_TYPES.find(v => v.value === type) || VEHICLE_TYPES[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <div className="text-lg text-gray-600">Загрузка...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}/admin`}>
                <Button variant="ghost" size="sm" data-testid="back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Назад
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Управление доставкой</h1>
                <p className="text-sm text-gray-500">Курьеры и доставки</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Button 
                onClick={() => {
                  resetCourierForm();
                  setShowCourierDialog(true);
                }} 
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="add-courier-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Добавить курьера
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <Navigation className="w-4 h-4" />
              Обзор дня
            </TabsTrigger>
            <TabsTrigger value="couriers" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Курьеры ({couriers.length})
            </TabsTrigger>
            <TabsTrigger value="deliveries" className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Доставки
            </TabsTrigger>
            <TabsTrigger value="ops" className="flex items-center gap-2" data-testid="delivery-tab-ops">
              <BarChart3 className="w-4 h-4" />
              Аналитика
            </TabsTrigger>
            {canEditRoutes && (
              <TabsTrigger value="routes" className="flex items-center gap-2" data-testid="delivery-tab-routes">
                <MapPin className="w-4 h-4" />
                Маршруты
              </TabsTrigger>
            )}
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {dashboard && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-500">Доставок на сегодня</div>
                          <div className="text-3xl font-bold">{dashboard.total_deliveries}</div>
                        </div>
                        <Truck className="w-10 h-10 text-emerald-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className={dashboard.unassigned_count > 0 ? 'border-orange-300' : ''}>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-500">Без курьера</div>
                          <div className={`text-3xl font-bold ${dashboard.unassigned_count > 0 ? 'text-orange-600' : ''}`}>
                            {dashboard.unassigned_count}
                          </div>
                        </div>
                        <Clock className="w-10 h-10 text-orange-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-500">В пути</div>
                          <div className="text-3xl font-bold text-purple-600">
                            {dashboard.status_counts?.in_transit || 0}
                          </div>
                        </div>
                        <Navigation className="w-10 h-10 text-purple-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-500">Доставлено</div>
                          <div className="text-3xl font-bold text-green-600">
                            {dashboard.status_counts?.delivered || 0}
                          </div>
                        </div>
                        <CheckCircle className="w-10 h-10 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Courier Workload */}
                <Card>
                  <CardHeader>
                    <CardTitle>Нагрузка курьеров</CardTitle>
                    <CardDescription>Сегодня, {dashboard.date}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {dashboard.courier_workload.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Нет активных курьеров</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {dashboard.courier_workload.map((courier) => (
                          <div key={courier.courier_id} className="p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold">{courier.courier_name}</h4>
                              <Badge variant="outline">{courier.total} заказов</Badge>
                            </div>
                            <div className="flex gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">Ожидает:</span>{' '}
                                <span className="font-medium">{courier.pending}</span>
                              </div>
                              <div>
                                <span className="text-purple-500">В пути:</span>{' '}
                                <span className="font-medium">{courier.in_transit}</span>
                              </div>
                              <div>
                                <span className="text-green-500">Готово:</span>{' '}
                                <span className="font-medium">{courier.completed}</span>
                              </div>
                            </div>
                            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-green-500 transition-all"
                                style={{ width: courier.total > 0 ? `${(courier.completed / courier.total) * 100}%` : '0%' }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Couriers Tab */}
          <TabsContent value="couriers">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {couriers.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center text-gray-500">
                    <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-lg font-medium">Нет курьеров</p>
                    <p className="text-sm">Добавьте курьеров для управления доставкой</p>
                  </CardContent>
                </Card>
              ) : (
                couriers.map((courier) => {
                  const VehicleIcon = getVehicleConfig(courier.vehicle_type).icon;
                  return (
                    <Card key={courier.id} className={!courier.is_active ? 'opacity-60' : ''} data-testid={`courier-card-${courier.id}`}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                              <User className="w-6 h-6 text-emerald-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold">{courier.name}</h3>
                              <div className="flex items-center gap-1 text-sm text-gray-500">
                                <VehicleIcon className="w-3 h-3" />
                                {getVehicleConfig(courier.vehicle_type).label}
                              </div>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditCourier(courier)}>
                                Редактировать
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleDeleteCourier(courier.id)}
                                className="text-red-600"
                              >
                                Удалить
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Phone className="w-4 h-4" />
                            {courier.phone}
                          </div>
                          {courier.email && (
                            <div className="flex items-center gap-2 text-gray-600">
                              @{courier.email}
                            </div>
                          )}
                        </div>

                        <div className="mt-4 pt-3 border-t flex items-center justify-between">
                          <div className="text-sm">
                            <span className="text-gray-500">Доставок:</span>{' '}
                            <span className="font-medium">{courier.completed_deliveries || 0}</span>
                          </div>
                          <Badge className={courier.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                            {courier.is_active ? 'Активен' : 'Неактивен'}
                          </Badge>
                        </div>

                        {courier.active_deliveries > 0 && (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-blue-700">
                            В работе: {courier.active_deliveries} доставок
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Deliveries Tab */}
          <TabsContent value="deliveries">
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="w-[150px]">
                    <Label className="text-sm text-gray-600">Дата</Label>
                    <Input
                      type="date"
                      value={deliveryFilters.date}
                      onChange={(e) => setDeliveryFilters(prev => ({ ...prev, date: e.target.value }))}
                    />
                  </div>

                  <div className="w-[150px]">
                    <Label className="text-sm text-gray-600">Статус</Label>
                    <Select 
                      value={deliveryFilters.status} 
                      onValueChange={(v) => setDeliveryFilters(prev => ({ ...prev, status: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Все</SelectItem>
                        {DELIVERY_STATUSES.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-[180px]">
                    <Label className="text-sm text-gray-600">Курьер</Label>
                    <Select 
                      value={deliveryFilters.courier_id} 
                      onValueChange={(v) => setDeliveryFilters(prev => ({ ...prev, courier_id: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Все курьеры</SelectItem>
                        {couriers.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button variant="outline" size="sm" onClick={fetchDeliveries}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Обновить
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Deliveries List */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="deliveries-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Заказ</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Клиент</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Адрес</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Время</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Курьер</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Статус</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Действия</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {deliveries.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                            <Truck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p className="text-lg font-medium">Нет доставок</p>
                          </td>
                        </tr>
                      ) : (
                        deliveries.map((delivery) => {
                          const statusConfig = getStatusConfig(delivery.status);
                          return (
                            <tr key={delivery.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <span className="font-mono text-sm font-medium text-emerald-700">
                                  {delivery.order_number}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="font-medium">{delivery.customer_name}</div>
                                <div className="text-sm text-gray-500">{delivery.customer_phone}</div>
                              </td>
                              <td className="px-4 py-3 max-w-[200px]">
                                <p className="text-sm text-gray-600 truncate" title={delivery.delivery_address}>
                                  {delivery.delivery_address || '—'}
                                </p>
                              </td>
                              <td className="px-4 py-3 text-center text-sm">
                                {delivery.delivery_time || '—'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {delivery.courier_name ? (
                                  <Badge variant="outline">{delivery.courier_name}</Badge>
                                ) : (
                                  <Badge className="bg-orange-100 text-orange-800">Не назначен</Badge>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <MoreHorizontal className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => {
                                      setSelectedDelivery(delivery);
                                      setShowAssignDialog(true);
                                    }}>
                                      <UserPlus className="w-4 h-4 mr-2" />
                                      Назначить курьера
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleDeliveryStatus(delivery.id, 'in_transit')}>
                                      <Navigation className="w-4 h-4 mr-2" />
                                      В пути
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDeliveryStatus(delivery.id, 'delivered')}>
                                      <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                                      Доставлен
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDeliveryStatus(delivery.id, 'failed')}>
                                      <XCircle className="w-4 h-4 mr-2 text-red-600" />
                                      Не доставлен
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-500">
                  Показано {deliveries.length} из {total}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Назад
                  </Button>
                  <span className="px-3 py-1 text-sm">
                    {page} из {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Далее
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Ops Analytics Tab */}
          <TabsContent value="ops" data-testid="delivery-ops-content">
            {(() => {
              const sm = ops?.summary || {};
              const fmtKm = (n) => `${(n ?? 0).toLocaleString('ru-RU')} км`;
              return (
                <>
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div>
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Activity className="w-5 h-5 text-emerald-700" /> Операционная аналитика доставки
                      </h3>
                      <p className="text-sm text-gray-500">Эффективность маршрутов: километраж, объёмы, проблемы</p>
                    </div>
                    <Select value={opsDays} onValueChange={setOpsDays}>
                      <SelectTrigger className="w-[150px]" data-testid="ops-period-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">7 дней</SelectItem>
                        <SelectItem value="30">30 дней</SelectItem>
                        <SelectItem value="90">90 дней</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* KPI */}
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
                    <Card data-testid="ops-kpi-routes"><CardContent className="pt-4">
                      <div className="text-xs text-gray-500 flex items-center gap-1"><Route className="w-3 h-3" /> Маршрутов</div>
                      <div className="text-2xl font-bold">{sm.routes ?? '—'}</div>
                    </CardContent></Card>
                    <Card data-testid="ops-kpi-deliveries"><CardContent className="pt-4">
                      <div className="text-xs text-gray-500 flex items-center gap-1"><Package className="w-3 h-3" /> Доставок</div>
                      <div className="text-2xl font-bold">{sm.deliveries ?? '—'}</div>
                    </CardContent></Card>
                    <Card data-testid="ops-kpi-total-km"><CardContent className="pt-4">
                      <div className="text-xs text-gray-500">Всего км</div>
                      <div className="text-2xl font-bold text-emerald-700">{fmtKm(sm.total_km)}</div>
                    </CardContent></Card>
                    <Card data-testid="ops-kpi-avg-route"><CardContent className="pt-4">
                      <div className="text-xs text-gray-500">Км / маршрут</div>
                      <div className="text-2xl font-bold">{sm.avg_km_route ?? '—'}</div>
                    </CardContent></Card>
                    <Card data-testid="ops-kpi-avg-delivery"><CardContent className="pt-4">
                      <div className="text-xs text-gray-500">Км / доставку</div>
                      <div className="text-2xl font-bold">{sm.avg_km_delivery ?? '—'}</div>
                    </CardContent></Card>
                  </div>

                  {sm.data_note && (
                    <div className="flex items-start gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4" data-testid="ops-data-note">
                      <Info className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{sm.data_note}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Per-driver */}
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4" /> По водителям</CardTitle></CardHeader>
                      <CardContent className="p-0">
                        <table className="w-full text-sm" data-testid="ops-drivers-table">
                          <thead className="bg-gray-50 border-b"><tr>
                            <th className="text-left p-3 font-medium">Водитель</th>
                            <th className="text-right p-3 font-medium">Доставок</th>
                            <th className="text-right p-3 font-medium">Км</th>
                            <th className="text-right p-3 font-medium">Км/дост.</th>
                            <th className="text-right p-3 font-medium">Проблем</th>
                          </tr></thead>
                          <tbody className="divide-y">
                            {(ops?.drivers || []).map((d, i) => (
                              <tr key={i} className="hover:bg-gray-50" data-testid={`ops-driver-${i}`}>
                                <td className="p-3 font-medium">{d.driver}</td>
                                <td className="p-3 text-right">{d.deliveries}</td>
                                <td className="p-3 text-right">{d.km}</td>
                                <td className="p-3 text-right text-gray-600">{d.avg_km}</td>
                                <td className={`p-3 text-right ${d.problems ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>{d.problems}</td>
                              </tr>
                            ))}
                            {!ops?.drivers?.length && <tr><td colSpan="5" className="p-6 text-center text-gray-400">Нет данных</td></tr>}
                          </tbody>
                        </table>
                      </CardContent>
                    </Card>

                    {/* Per-route */}
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Route className="w-4 h-4" /> По маршрутам</CardTitle></CardHeader>
                      <CardContent className="p-0">
                        <div className="max-h-[420px] overflow-y-auto">
                          <table className="w-full text-sm" data-testid="ops-routes-table">
                            <thead className="bg-gray-50 border-b sticky top-0"><tr>
                              <th className="text-left p-3 font-medium">Дата</th>
                              <th className="text-left p-3 font-medium">Водитель</th>
                              <th className="text-right p-3 font-medium">Точек</th>
                              <th className="text-right p-3 font-medium">Км</th>
                              <th className="text-right p-3 font-medium">Км/точку</th>
                            </tr></thead>
                            <tbody className="divide-y">
                              {(ops?.routes || []).map((r, i) => (
                                <tr key={r.route_id || i} className="hover:bg-gray-50" data-testid={`ops-route-${i}`}>
                                  <td className="p-3">{r.date}</td>
                                  <td className="p-3 text-gray-600">{r.driver}</td>
                                  <td className="p-3 text-right">{r.deliveries}/{r.stops}</td>
                                  <td className="p-3 text-right font-medium">{r.km}</td>
                                  <td className="p-3 text-right text-gray-600">{r.km_per_stop ?? '—'}</td>
                                </tr>
                              ))}
                              {!ops?.routes?.length && <tr><td colSpan="5" className="p-6 text-center text-gray-400">Нет данных</td></tr>}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </>
              );
            })()}
          </TabsContent>


          {canEditRoutes && (
            <TabsContent value="routes" data-testid="delivery-routes-content">
              <RouteEditorPanel />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Courier Dialog */}
      <Dialog open={showCourierDialog} onOpenChange={setShowCourierDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCourier ? 'Редактирование курьера' : 'Новый курьер'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveCourier} className="space-y-4">
            <div>
              <Label>Имя *</Label>
              <Input
                value={courierForm.name}
                onChange={(e) => setCourierForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Имя курьера"
                required
              />
            </div>

            <div>
              <Label>Телефон *</Label>
              <Input
                value={courierForm.phone}
                onChange={(e) => setCourierForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+7 (___) ___-__-__"
                required
              />
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={courierForm.email}
                onChange={(e) => setCourierForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@example.com"
              />
            </div>

            <div>
              <Label>Транспорт</Label>
              <Select
                value={courierForm.vehicle_type}
                onValueChange={(v) => setCourierForm(prev => ({ ...prev, vehicle_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VEHICLE_TYPES.map(v => (
                    <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={courierForm.is_active}
                onCheckedChange={(checked) => setCourierForm(prev => ({ ...prev, is_active: checked }))}
              />
              <Label>Активен</Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCourierDialog(false)}>
                Отмена
              </Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800">
                {editingCourier ? 'Сохранить' : 'Добавить'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Courier Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Назначить курьера</DialogTitle>
          </DialogHeader>
          
          {selectedDelivery && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Заказ: <span className="font-mono font-medium">{selectedDelivery.order_number}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Клиент: <span className="font-medium">{selectedDelivery.customer_name}</span>
                </p>
              </div>

              <div>
                <Label className="mb-2 block">Выберите курьера</Label>
                <div className="space-y-2">
                  {couriers.filter(c => c.is_active).map((courier) => (
                    <div 
                      key={courier.id}
                      className="p-3 border rounded-lg cursor-pointer hover:bg-emerald-50 hover:border-emerald-300 transition-colors"
                      onClick={() => handleAssignDelivery(courier.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <div className="font-medium">{courier.name}</div>
                            <div className="text-sm text-gray-500">{courier.phone}</div>
                          </div>
                        </div>
                        {courier.active_deliveries > 0 && (
                          <Badge variant="outline">{courier.active_deliveries} в работе</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminDelivery;
